export function debounce(func, delay) {
    let timeoutId;

    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
}

// Check if the passed string is empty or null
export function isEmptyValue(str) {
    str = str?.trim();
    return !(str && str.length > 0 && str !== 'null' && str !== 'undefined');
}


// Detect if the device is on iOS
export const isIos = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
};

// Detect if the device is on Android
export const isAndroid = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /android/.test(userAgent);
}

// Detect if the device is on Android
export const isSafari = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /^((?!chrome|android).)*safari/i.test(userAgent);
}

export class FAQSearch {
    constructor(faqs, searchableKeys = ['question', 'answer'], minCharLength = 3) {
        this.faqs = faqs;
        this.searchableKeys = searchableKeys;
        this.minCharLength = minCharLength;
    }

    search(query) {
        query = query.trim();
        if (query.length < this.minCharLength) {
            // Reset the highlighted elements and return original list
            return this.faqs.map((faq) => ({
                ...faq,
                highlightedElement: (faq.element[0] ?? faq.element).cloneNode(true),
                relevancyScore: 0,
            }));
        }

        const queryWords = query
            .toLowerCase()
            .split(/\s+/)
            // Exclude words which are too short
            .filter((word) => word.length > 1);

        return this.faqs
            .map((faq) => {
                const relevancyScore = this.calculateRelevancy(queryWords, faq);
                if (relevancyScore > 0) {
                    faq.highlightedElement = (faq.element[0] ?? faq.element).cloneNode(true);
                    faq.highlightedElement.innerHTML = this.highlightMatches(
                        faq.highlightedElement.innerHTML,
                        queryWords
                    );
                }
                return { ...faq, relevancyScore };
            })
            .filter((result) => result.relevancyScore > 0)
            .sort((a, b) => b.relevancyScore - a.relevancyScore);
    }

    calculateRelevancy(queryWords, faq) {
        let score = 0;
        const weightQuestion = 2;
        const weightAnswer = 1;

        queryWords.forEach((word) => {
            const contentQuestion = faq.question.toLowerCase();
            const contentAnswer = faq.answer.toLowerCase();

            if (contentQuestion.includes(word)) {
                score += weightQuestion;
            }

            if (contentAnswer.includes(word)) {
                score += weightAnswer;
            }
        });

        // Additional points for multiple word matches
        if (score > queryWords.length * weightQuestion) {
            score += score / 2;
        }

        return score;
    }

    highlightMatches(html, queryWords) {
        const uniqueWords = Array.from(new Set(queryWords));
        uniqueWords.forEach((word) => {
            const escapedWord = word.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            const regex = new RegExp(`(>)([^<]*?)(${escapedWord})([^<]*?)(<)`, 'gi');
            html = html.replace(regex, (match, p1, p2, p3, p4, p5) => `${p1}${p2}<mark>${p3}</mark>${p4}${p5}`);
        });
        return html;
    }
}

export function createCookie(name, value, days) {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toGMTString();
    }
    document.cookie = name + '=' + value + expires + '; path=/;';
}
export function readCookie(name, default_value = null) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return default_value;
}
export function eraseCookie(name) {
    createCookie(name, '', -1);
}

export function getUrlParameter(param) {
    const params = new URLSearchParams(window.location.search);
    return params.has(param) ? params.get(param) : false;
}

export function bodyHasAnyClass(classArray) {
    const bodyClasses = document.body.classList;

    return classArray.some(cls => bodyClasses.contains(cls));
}


// function to wait for an element in the DOM and execute a callback
export function waitForElement(selector, callback, intervalTime = 200) {
    const element = document.querySelector(selector);
    if (element) {
        callback(element); // execute the callback if the element is already in the DOM
    } else {
        const interval = setInterval(() => {
            const updatedElement = document.querySelector(selector);
            if (updatedElement) {
                callback(updatedElement); // execute the callback once the element is found
                clearInterval(interval); // clear the interval once the element is found and stop checking
            }
        }, intervalTime);
    }
}